export const API = import.meta.env.VITE_API_URL ?? "/api";

const TOKEN_KEY = "k6waifus.app_token";
const sessionKey = (roomId: number) => `k6waifus.session.${roomId}`;
export const getAppToken = () => localStorage.getItem(TOKEN_KEY) ?? "";
export const setAppToken = (token: string) => localStorage.setItem(TOKEN_KEY, token.trim());
export const clearAppToken = () => localStorage.removeItem(TOKEN_KEY);

function getRoomSession(path: string): string | null {
  const roomId = path.match(/^\/rooms\/(\d+)/)?.[1];
  if (!roomId) return null;
  const key = sessionKey(Number(roomId));
  let value = localStorage.getItem(key);
  if (!value) {
    value = crypto.randomUUID();
    localStorage.setItem(key, value);
  }
  return value;
}

function withAuth(init?: RequestInit): RequestInit {
  const headers = new Headers(init?.headers);
  const token = getAppToken();
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return { ...init, headers };
}

export interface Character {
  id?: number;
  name: string;
  avatar_emoji: string;
  avatar_key?: string | null;
  color: string;
  traits: string[];
  speech_style: string[];
  backstory: string;
  relationship: string;
  boundaries: string[];
  nickname: string;
  appearance: string;
  voice: string;
  clothing: string;
  setting: string;
  greeting: string;
  narrator_instructions: string;
  description: string;
  personality: string;
  scenario: string;
  example_dialogs: Array<{ user: string; assistant: string }>;
  first_message: string;
  author_note: string;
  format_rules: string;
}

export interface Room {
  id: number;
  name: string;
  character_ids: number[];
  scenario?: string;
}

export interface Message {
  id: number | string;
  turn_id?: number | null;
  role: "user" | "assistant";
  content: string;
  character_id: number | null;
  is_error?: boolean;
  retry_text?: string;
  source_message_id?: number | string;
  created_at?: string;
  is_draft?: boolean;
}

export const api = async (path: string, init?: RequestInit) => {
  const nextInit = withAuth(init);
  const roomSession = getRoomSession(path);
  if (roomSession) (nextInit.headers as Headers).set("X-Session-Id", roomSession);
  const res = await fetch(`${API}${path}`, nextInit);
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    if (res.status === 401) {
      clearAppToken();
      window.dispatchEvent(new Event("k6waifus-auth-expired"));
    }
    const error = new Error(data.error ?? `Request failed (${res.status})`) as Error & { data?: unknown; status?: number };
    error.data = data;
    error.status = res.status;
    throw error;
  }
  return data;
};

export const avatarUrl = (id: number) => `${API}/characters/${id}/avatar`;

export const uploadAvatar = async (id: number, file: File) => {
  const res = await fetch(`${API}/characters/${id}/avatar`, {
    method: "PUT",
    headers: { "Content-Type": file.type, Authorization: `Bearer ${getAppToken()}` },
    body: file
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error ?? `Upload failed (${res.status})`);
  return data as { avatar_key: string };
};

export const deleteAvatar = async (id: number) => api(`/characters/${id}/avatar`, { method: "DELETE" });

export const deleteMessage = (roomId: number, messageId: number | string) =>
  api(`/rooms/${roomId}/messages/${messageId}`, { method: "DELETE" });

export const deleteTurn = (roomId: number, turnId: number) =>
  api(`/rooms/${roomId}/turns/${turnId}`, { method: "DELETE" });

export const editTurn = (roomId: number, turnId: number, message: string, onDelta: (delta: string) => void) =>
  streamChatRequest(`/rooms/${roomId}/turns/${turnId}`, message, onDelta, "PATCH");

export const resetRoom = (roomId: number) =>
  api(`/rooms/${roomId}/reset`, { method: "POST" });

export const deleteRoom = (roomId: number) =>
  api(`/rooms/${roomId}`, { method: "DELETE" });

export const post = (path: string, body: unknown) =>
  api(path, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body)
  });

export async function streamChat(
  roomId: number,
  message: string,
  onDelta: (delta: string) => void
): Promise<{ replies: Message[]; provider?: string }> {
  return streamChatRequest(`/rooms/${roomId}/chat`, message, onDelta, "POST");
}

async function streamChatRequest(path: string, message: string, onDelta: (delta: string) => void, method: "POST" | "PATCH") {
  const res = await fetch(`${API}${path}`, {
    method,
    headers: { "Content-Type": "application/json", Accept: "text/event-stream", Authorization: `Bearer ${getAppToken()}`, ...(getRoomSession(path) ? { "X-Session-Id": getRoomSession(path)! } : {}) },
    body: JSON.stringify({ message, stream: method === "POST" })
  });
  if (!res.ok || !res.body) throw new Error(`Request failed (${res.status})`);
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let event = "";
  let result: { replies: Message[]; provider?: string } | null = null;
  while (true) {
    const chunk = await reader.read();
    buffer += decoder.decode(chunk.value ?? new Uint8Array(), { stream: !chunk.done });
    const records = buffer.split("\n\n");
    buffer = records.pop() ?? "";
    for (const record of records) {
      const eventLine = record.split("\n").find((line) => line.startsWith("event:"));
      const dataLine = record.split("\n").find((line) => line.startsWith("data:"));
      if (eventLine) event = eventLine.slice(6).trim();
      if (!dataLine) continue;
      const data = JSON.parse(dataLine.slice(5).trim()) as { delta?: string; replies?: Message[]; provider?: string; message?: string };
      if (event === "delta" && data.delta) onDelta(data.delta);
      if (event === "complete") result = { replies: data.replies ?? [], provider: data.provider };
      if (event === "error") throw new Error(data.message ?? "Streaming failed");
    }
    if (chunk.done) break;
  }
  if (!result) throw new Error("Stream ended without a response");
  return result;
}

export function emptyChar(): Character {
  return {
    name: "",
    avatar_emoji: "🌸",
    color: "#a78bfa",
    traits: [],
    speech_style: [],
    backstory: "",
    relationship: "",
    boundaries: []
    ,nickname: ""
    ,appearance: ""
    ,voice: ""
    ,clothing: ""
    ,setting: ""
    ,greeting: ""
    ,narrator_instructions: ""
    ,description: ""
    ,personality: ""
    ,scenario: ""
    ,example_dialogs: []
    ,first_message: ""
    ,author_note: ""
    ,format_rules: ""
  };
}